import React, { createContext, useContext, useState } from 'react';
import { RestfulProvider } from 'restful-react';
import { jwtDecode as decodeJWT } from 'jwt-decode';
import fixErrorData from './fixErrorData';
import checkIfWafError from './checkIfWafError';
import dynamicConfig from '../config/dynamicConfig';

interface DecodedJWT {
    exp: number;
    [key: string]: any;
}

interface ApiErrorContextInterface {
    apiError: boolean,
    setApiError: (apiError: boolean) => void;
    wafError: WafError,
}

interface WafError {
    wafErrorId: null|string,
    url: null|string,
}

// Store api token globally and clear it when it expires
let token: string|null;
let refetchTokenTimeout: number;
let refetchTokenPromise: Promise<void>;
function setToken(newToken: string|null, expiresIn?: number) {
    token = newToken;

    if (token) {
        localStorage.setItem('token', token);

        // Set clear timeout
        expiresIn = expiresIn ? expiresIn * 1000 : (decodeJWT(token) as DecodedJWT).exp * 1000 - Date.now();
        if (expiresIn <= 0) {
            // If it is already expired, recall the current method and set the token to null
            setToken(null);
        } else {
            // Else wait for it to expire
            window.clearTimeout(refetchTokenTimeout);
            refetchTokenTimeout = window.setTimeout(() => setToken(null), expiresIn);
        }
    } else {
        window.clearTimeout(refetchTokenTimeout);
        localStorage.removeItem('token');
    }
}

// Set initial token with the value saved in the local storage
setToken(localStorage.getItem('token'));

// Make api errors accessible from everywhere
export const ApiErrorContext = createContext({} as ApiErrorContextInterface)

export const useAtmApiRequestOptions = () => {
    const {setApiError} = useContext(ApiErrorContext)

    const headers: HeadersInit = {};

    // Return async function to fetch token right before the actual request
    return async () => {
        if (refetchTokenPromise) {
            // If there is already an ongoing refetch, wait for it
            await refetchTokenPromise;
        }

        if (!token) {
            // Set global promise so all other requests wait for us
            refetchTokenPromise = new Promise(async (resolve, reject) => {
                try {
                    // Create login data
                    const formData = new URLSearchParams();
                    formData.append('grant_type', 'client_credentials');
                    formData.append('client_id', dynamicConfig.clientId);
                    formData.append('client_secret', dynamicConfig.clientSecret);

                    // Get token
                    const response = await fetch(dynamicConfig.tokenUrl, {
                        method: 'POST',
                        mode: 'cors',
                        body: formData,
                    });

                    const {expires_in, access_token} = await response.json();

                    setToken(access_token, expires_in);

                    resolve();
                } catch (error) {
                    setApiError(true);

                    reject(error);
                }
            });
            await refetchTokenPromise;
        }

        headers['Authorization'] = 'Bearer ' + token;

        return {
            headers,
        };
    }
};

type ApiProviderProps = {
    children: React.ReactNode;
}

const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
    const [apiError, setApiError] = useState(false);
    const [wafError, setWafError] = useState<WafError>({
        wafErrorId: null,
        url: null,
    });

    function onError(error: { status?: number, data: unknown }, retry: () => Promise<unknown>, response?: Response) {
        // Manually parse error data as JSON if needed
        if (response?.headers) {
            fixErrorData(response.headers, error);
        }

        // If WAF error, show custom info the user
        const wafErrorId = checkIfWafError(error);
        if (wafErrorId) {
            setWafError({
                wafErrorId,
                url: response?.url ?? null,
            });
        }
    }

    return (
        <ApiErrorContext value={{
            apiError,
            setApiError,
            wafError,
        }}>
            {/* @ts-ignore */}
            <RestfulProvider
                base={dynamicConfig.atmUrl}
                onError={onError}
            >
                {children}
            </RestfulProvider>
        </ApiErrorContext>
    );
};

export default ApiProvider;
